/**
 * Replies API - Create Operations
 *
 * Adding new replies to comments.
 */

import {
  collection,
  doc,
  getDoc,
  writeBatch,
  increment,
  serverTimestamp,
} from "firebase/firestore";
import { db, auth } from "../../firebaseConfig";
import { ensureFreshToken } from "../authUtils";
import { getDisplayName } from "../../utils/userDisplay";
import { retryOp } from "./retry.js";

/**
 * Creates a new reply on a comment.
 *
 * Uses a Firestore batch to atomically:
 *   1. Create the reply document
 *   2. Increment the parent comment's replyCount
 *   3. Increment the post's commentCount (multi-level counter)
 *
 * Wrapped in retryOp for transient-error resilience.
 *
 * @param {string} commentId
 * @param {{
 *   userId?: string,
 *   text: string,
 *   username?: string,
 *   displayName?: string,
 *   name?: string,
 *   profession?: string,
 *   photoURL?: string,
 *   parentReplyId?: string|null,
 *   postId?: string,  // Optional: if not provided, will be fetched from the comment
 * }} reply
 * @returns {Promise<{
 *   id: string,
 *   commentId: string,
 *   postId: string,
 *   userId: string,
 *   username: string,
 *   profession: string,
 *   photoURL: string,
 *   text: string,
 *   timestamp: Date,
 *   likes: number,
 *   isLiked: boolean,
 *   isOptimistic: false,
 *   parentReplyId: string|null
 * }>} The complete reply object with server-generated ID and client timestamp.
 */
export const addReply = async (commentId, reply) => {
  return retryOp(
    async () => {
      if (!commentId) throw new Error("Comment ID is required");
      if (!reply) throw new Error("Reply data is required");

      // ── Phase 1: Data Sanitization (Source of Truth) ──
      const trimmedText = (reply.text || "").trim();

      if (!trimmedText) {
        throw new Error("Reply text cannot be empty");
      }
      if (trimmedText.length > 2000) {
        throw new Error(
          `Reply exceeds maximum length of 2000 characters (got ${trimmedText.length})`,
        );
      }

      await ensureFreshToken();

      const repliesRef = collection(db, "replies");
      const replyRef = doc(repliesRef);
      const commentRef = doc(db, "comments", commentId);

      // Always use Firebase auth UID as the authoritative source.
      const authUser = auth?.currentUser;
      if (!authUser) {
        throw new Error("No authenticated user — cannot create reply");
      }
      const authUid = authUser.uid;

      const username = getDisplayName({
        candidates: [reply?.username, reply?.displayName, reply?.name],
      });

      // Prefer caller-provided postId to avoid an extra read; fall back to comment lookup.
      let postId = reply.postId || null;
      if (!postId) {
        const commentDoc = await getDoc(commentRef);
        if (!commentDoc.exists()) {
          throw new Error(`Comment ${commentId} not found`);
        }
        const commentData = commentDoc.data();
        postId = commentData.postId;
      }

      if (!postId) {
        throw new Error(
          "Could not determine postId for multi-level counter update",
        );
      }

      const postRef = doc(db, "posts", postId);

      // ATOMIC MULTI-LEVEL BATCH:
      // Operation A: Create the reply document
      // Operation B: Increment parent comment's replyCount
      // Operation C: Increment post's commentCount (total activity counter)
      const batch = writeBatch(db);

      // Generate idempotency key for client-side deduplication (Phase 1 fix)
      const clientRequestId = reply.clientRequestId || `${authUid}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

      // A: Create reply (with idempotency key for deduplication)
      batch.set(replyRef, {
        commentId,
        parentReplyId: reply.parentReplyId || null,
        userId: authUid,
        username,
        userProfession: reply.profession || "",
        photoURL: reply.photoURL || "",
        text: trimmedText,
        timestamp: serverTimestamp(),
        likes: 0,
        clientRequestId, // Phase 1: Idempotency key
      });

      // B: Increment comment's replyCount
      batch.update(commentRef, { replyCount: increment(1) });

      // C: Increment post's commentCount (multi-level: includes all replies)
      batch.update(postRef, { commentCount: increment(1) });

      try {
        await batch.commit();
      } catch (batchErr) {
        console.error("[addReply] batch commit failed:", batchErr.code, batchErr.message);
        throw batchErr;
      }

      // Return the full reply object for optimistic UI reconciliation
      return {
        id: replyRef.id,
        commentId,
        postId, // Include postId for Redux sync
        parentReplyId: reply.parentReplyId || null,
        userId: authUid,
        username: username || "User",
        userProfession: reply.profession || "",
        profession: reply.profession || "",
        photoURL: reply.photoURL || "",
        text: trimmedText,
        timestamp: new Date(),
        likes: 0,
        isLiked: false,
        isOptimistic: false,
        isEdited: false,
        updatedAt: null,
        deletedAt: null,
      };
    },
    { maxRetries: 3, baseDelay: 1500 },
  );
};
